/**
 * DDukDoc 승인 게이트 — 상담원 입력칸에 AI 초안을 채우는 두 가지 판단.
 *
 * 컴포넌트 밖에 두는 이유는 하나다: 이 두 판단이 틀리면 **조용히** 틀린다. 신호를 놓치면
 * 노트만 뜨고 입력칸은 비어 있고, 덮어쓰기를 잘못하면 상담원이 친 문장이 사라진다. 둘 다
 * 오류를 내지 않으므로 화면을 열어 봐서는 알 수 없다. 그래서 컴포넌트를 띄우지 않고
 * 검증할 수 있는 순수 함수로 떼어 둔다.
 */

/**
 * 승인 게이트가 대화에 남기는 비공개 노트의 머리말.
 *
 * `packages/conversation-engine/src/pipeline.ts`의 `DRAFT_NOTE_PREFIX`와 **같아야 한다.**
 * 두 저장소에 같은 리터럴이 있으므로 한쪽만 바꾸면 알림 없이 실시간 갱신이 멈춘다 —
 * `__tests__/draft-note-prefix-contract.test.ts`가 그 어긋남에서 깨진다.
 */
export const DDUKDOC_DRAFT_NOTE_PREFIX = '[AI 초안 · 승인 전]';

/**
 * 초안 노트를 본 뒤 큐 행이 보일 때까지 되묻는 간격(ms).
 *
 * 노트 미러는 파이프라인 안에서 defer로 나가고 큐 행은 그 뒤 라우트가 쓴다 — **노트가 먼저
 * 도착하는 것이 정상**이다. 한 번만 물으면 아직 없는 행 대신 직전 초안이 돌아오고, 그것은
 * 이미 입력칸에 있는 것과 같아 아무 일도 일어나지 않는다. 다시 부를 계기도 없으므로 입력칸은
 * 낡은 초안을 든 채 굳는다.
 *
 * 간격은 늘려 잡는다. 대부분은 첫 되묻기에서 끝나고, 늦어지는 경우에만 뒤쪽 간격을 쓴다.
 * 총 대기는 약 9초이며 그 안에 못 보면 그 턴은 포기한다 — 무한히 되묻는 것보다 낫다
 * (다음 초안이 오면 그때 다시 신호가 온다).
 */
export const DDUKDOC_DRAFT_RETRY_DELAYS = [700, 1400, 2800, 4000];

/**
 * 이 대화에 마지막으로 도착한 AI 초안 노트의 id. 없으면 null.
 *
 * 새 초안이 생겼다는 신호로 이것을 쓴다. "고객 메시지가 왔다"를 신호로 삼으면 안 된다 —
 * 그 순간 파이프라인은 아직 답을 만들지 않았고, 그때 창구를 부르면 방금 무의미해진
 * **이전 초안**이 돌아온다. 노트가 보이는 시점은 초안이 큐에 적재된 뒤다.
 *
 * @param {Array<{id?: number, private?: boolean, content?: string}>} messages
 * @returns {number|null}
 */
export function findLatestDraftNoteId(messages) {
  const list = Array.isArray(messages) ? messages : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const message = list[i];
    if (
      message &&
      message.private &&
      String(message.content || '').startsWith(DDUKDOC_DRAFT_NOTE_PREFIX)
    ) {
      return message.id ?? null;
    }
  }
  return null;
}

/**
 * 초안 본문의 지문. 같은 본문이면 같은 값이 나온다.
 *
 * **본문 대신 이것을 저장한다.** 새로고침해도 "지금 입력칸에 있는 것이 우리가 넣은
 * 초안인가"를 알아야 하는데, 그러자고 승인 전 평문을 한 벌 더 브라우저에 쌓을 이유는
 * 없다. 지문은 되돌릴 수 없고 비교에는 충분하다.
 *
 * 충돌해도 잃는 것은 없다 — 상담원이 우연히 같은 지문의 글을 썼다면 그것은 우리가 넣은
 * 초안과 사실상 같은 글이다. 그래서 암호학적 해시가 아니라 FNV-1a면 된다.
 *
 * @param {string|null|undefined} text
 * @returns {string} 8자리 16진수. 빈 문자열이면 ''.
 */
export function draftDigest(text) {
  const body = (text || '').trim();
  if (!body) return '';
  let hash = 0x811c9dc5;
  for (let i = 0; i < body.length; i += 1) {
    hash ^= body.charCodeAt(i);
    // FNV prime 16777619. Math.imul로 32비트 곱을 유지한다.
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * 지금 입력칸을 새 초안으로 갈아도 되는가.
 *
 * 규칙은 둘이다.
 *
 * **① 같은 큐 행은 새 초안이 아니다.** 창구가 직전에 넣은 것과 같은 행을 돌려주면 가져올
 * 것이 없다. 이 검사가 없으면 상담원이 일부러 지운 입력칸을 다음 조회가 도로 채운다 —
 * 지운 것은 지운 것이다.
 *
 * **② 사람이 친 글자는 지우지 않는다.** 입력칸이 비었거나 그 내용의 지문이 우리가 직전에
 * 넣어 둔 초안의 지문과 같으면 갈아도 잃을 것이 없다(그것은 이미 지난 질문에 대한 답이다).
 * 한 글자라도 상담원이 손을 댔으면 그대로 둔다. 새 초안은 노트로 대화에 남아 있어
 * 사라지지 않고, 입력칸을 비우면 다음 신호에 채워진다.
 *
 * `currentBody`/`nextBody`는 **서명을 뺀 본문**이어야 한다. 보낸 직후 입력칸에는 서명만
 * 남는데, 그것을 "상담원이 쓴 글"로 읽으면 다음 초안이 영영 들어가지 못한다.
 *
 * @param {{currentBody?: string, injectedDigest?: string, nextBody?: string,
 *          queueId?: string|null, injectedQueueId?: string|null}} input
 * @returns {boolean}
 */
export function shouldReplaceComposer({
  currentBody,
  injectedDigest,
  nextBody,
  queueId,
  injectedQueueId,
} = {}) {
  // 양끝 공백은 비교에서 뺀다. 우리가 채운 초안은 자동저장이 `trimContent`로 다듬어
  // localStorage에 넣고 대화를 다시 열 때 그 다듬어진 값이 돌아온다 — 그대로 비교하면
  // **우리가 넣은 것을 남이 쓴 것으로 오인해** 낡은 초안이 영영 안 갈린다.
  const currentText = (currentBody || '').trim();
  const nextText = (nextBody || '').trim();

  if (!nextText) return false;
  // ① 같은 행을 다시 받은 것은 새 초안이 아니다.
  if (queueId && queueId === injectedQueueId) return false;
  // 같은 문장으로 갈아 끼우면 커서만 튄다.
  if (nextText === currentText) return false;
  // ② 비어 있으면 언제나 채운다.
  if (!currentText) return true;
  // 우리가 넣은 것 그대로일 때만 덮는다.
  return draftDigest(currentText) === (injectedDigest || '');
}

/**
 * 되물을 것인가.
 *
 * 되묻기는 "노트는 왔는데 큐 행이 아직 없다"는 창을 넘기려고 있다. 노트 미러는 파이프라인
 * 안에서 defer로 나가고 큐 행은 그 뒤 라우트가 쓰므로, 노트를 보자마자 물으면 아직 없는 새
 * 행 대신 직전 행(또는 없음)이 돌아오는 것이 정상이다.
 *
 * **채웠다고 멈추지 않는다.** 채운 것은 "무언가를 받았다"는 뜻이지 "가장 새 것을 받았다"는
 * 뜻이 아니다. 고객이 연달아 물으면 초안이 몇 초 사이에 두 번 커밋되고, 첫 번째를 채운 뒤
 * 멈추면 입력칸은 **지난 질문에 대한 답**을 든 채로 끝난다. 노트가 페이지 로딩 중에 도착한
 * 경우엔 watcher도 울리지 않아 다시 부를 계기조차 없다. 실제로 그렇게 멈췄다.
 *
 * 멈추는 경우는 둘뿐이다.
 *  · 예산 소진 — 무한히 되묻지 않는다.
 *  · **새 행이 보였는데도 쓰지 못했다** — 상담원이 입력칸에 손을 댔다는 뜻이고,
 *    몇 번을 되물어도 답은 같다.
 *
 * @param {{applied: boolean, queueId?: string|null,
 *          injectedQueueId?: string|null, retriesLeft?: number}} input
 * @returns {boolean}
 */
export function shouldRetryDraftSync({
  applied,
  queueId,
  injectedQueueId,
  retriesLeft = 0,
} = {}) {
  if (retriesLeft <= 0) return false;
  if (queueId && queueId !== injectedQueueId && !applied) return false;
  return true;
}

/** localStorage에 초안 출처를 담는 키. 대화별로 `{ queueId, digest }` 하나씩. */
export const DDUKDOC_DRAFT_PROVENANCE_KEY = 'ddukdoc_draft_provenance';

/** 출처 기록 상한. 넘으면 오래된 것부터 버린다 — 상담원 한 명이 하루에 여는 대화 수보다 넉넉하다. */
export const DDUKDOC_PROVENANCE_MAX = 200;

/**
 * 출처 기록을 상한 안으로 줄인다. 삽입 순서를 나이로 본다.
 *
 * **이 기록이 왜 브라우저에 남아야 하는가.** 우리가 채운 초안은 자동저장을 타고
 * localStorage에 들어가므로 새로고침해도 입력칸에 남는다. 그런데 "그것이 우리가 넣은
 * 것"이라는 사실이 메모리에만 있으면 새로고침과 함께 사라지고, 그 뒤로는 **모든 새 초안이
 * 상담원이 쓴 글로 오인돼 영영 들어가지 못한다.** 오류는 나지 않는다 — 그냥 갱신이 멈춘다.
 *
 * @param {Record<string, {queueId: string|null, digest: string}>} store
 * @returns {Record<string, {queueId: string|null, digest: string}>}
 */
export function trimProvenance(store) {
  const entries = Object.entries(store || {});
  if (entries.length <= DDUKDOC_PROVENANCE_MAX) return store || {};
  return Object.fromEntries(entries.slice(entries.length - DDUKDOC_PROVENANCE_MAX));
}
